import React, { useState, useRef, useCallback } from "react";
import { Upload, X, Image as ImageIcon, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
// import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

interface ImageUploadProps {
  value?: string;
  onChange: (value: string) => void;
  onFileChange?: (file: File) => void;
  className?: string;
  label?: string;
  required?: boolean;
  maxSize?: number; // en MB
  acceptedTypes?: string[];
}

/**
 * Componente para subida de imágenes con preview y validación
 *
 * Características:
 * - Drag & drop
 * - Preview de imagen
 * - Validación de formato y tamaño
 * - Botón para eliminar imagen
 * - Soporte para URL o archivo local
 */
export const ImageUpload: React.FC<ImageUploadProps> = ({
  value,
  onChange,
  onFileChange,
  className,
  label = "Imagen del producto",
  required = false,
  maxSize = 5, // 5MB por defecto
  acceptedTypes = ["image/jpeg", "image/png", "image/webp"],
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(value || null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Validar archivo
  const validateFile = useCallback(
    (file: File): boolean => {
      setError(null);

      // Validar tipo de archivo
      if (!acceptedTypes.includes(file.type)) {
        setError(
          `Formato no válido. Tipos permitidos: ${acceptedTypes
            .map((type) => type.split("/")[1])
            .join(", ")}`
        );
        return false;
      }

      // Validar tamaño
      if (file.size > maxSize * 1024 * 1024) {
        setError(`El archivo es muy grande. Tamaño máximo: ${maxSize}MB`);
        return false;
      }

      return true;
    },
    [acceptedTypes, maxSize]
  );

  // Manejar archivo seleccionado
  const handleFile = useCallback(
    (file: File) => {
      if (!validateFile(file)) return;

      // Crear preview
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setPreview(result);
        onChange(result);
        onFileChange?.(file);
      };
      reader.readAsDataURL(file);
    },
    [validateFile, onChange, onFileChange]
  );

  // Manejar drop de archivo
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        handleFile(files[0]);
      }
    },
    [handleFile]
  );

  // Manejar drag over
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  // Manejar drag leave
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  // Manejar selección de archivo
  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleFile(files[0]);
      }
    },
    [handleFile]
  );

  // Eliminar imagen
  const handleRemove = useCallback(() => {
    setPreview(null);
    setError(null);
    onChange("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [onChange]);

  // Abrir selector de archivo
  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return (
    <div className={cn("space-y-4", className)}>
      {label && (
        <Label className="text-sm font-medium">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </Label>
      )}

      {/* Área de subida */}
      <div
        className={cn(
          "border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer",
          isDragOver
            ? "border-primary bg-primary/5"
            : "border-muted-foreground/25 hover:border-primary/50",
          preview && "border-green-500 bg-green-50 dark:bg-green-950/20"
        )}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedTypes.join(",")}
          onChange={handleFileSelect}
          className="hidden"
        />

        {preview ? (
          // Preview de imagen
          <div className="space-y-4">
            <div className="relative inline-block">
              <img
                src={preview}
                alt="Preview"
                className="max-h-48 max-w-full rounded-lg object-cover"
              />
              <Button
                type="button"
                variant="destructive"
                size="sm"
                className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove();
                }}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              Haz clic para cambiar la imagen
            </p>
          </div>
        ) : (
          // Estado vacío
          <div className="space-y-2">
            <div className="mx-auto w-12 h-12 bg-muted rounded-full flex items-center justify-center">
              <ImageIcon className="h-6 w-6 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm font-medium">
                Arrastra una imagen aquí o haz clic para seleccionar
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                PNG, JPG, WEBP hasta {maxSize}MB
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Mensaje de error */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950/20 rounded-lg">
          <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Botones de acción */}
      {!preview && (
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleClick}
            className="flex items-center gap-2"
          >
            <Upload className="h-4 w-4" />
            Seleccionar Imagen
          </Button>
        </div>
      )}
    </div>
  );
};
